import {useEffect, useState} from 'react'
import {PammOfferType} from '../../graphql/types'
import {NetworkViewHeader} from '../PammOffersTree/components/NetworkViewHeader'
import {
  NETWORK_PANEL_ID,
  NetworkViewToggle,
  viewTabId,
} from '../PammOffersTree/components/NetworkViewToggle'
import {PammOffersTree} from '../PammOffersTree'
import {MainOfferControl} from '../PammOffersTree/MainOfferContext'
import {PammNetworkViewId} from '../PammOffersTree/types'
import {Root} from './PammNetworkTree.styled'

/** Display order of the segments: widest scope first, so the toggle reads master → agent. */
const VIEW_ORDER: PammNetworkViewId[] = ['master', 'agent']

type PammNetworkTreeProps = {
  /** The viewer's full Money Manager network; omitted or empty when they are not a master. */
  masterOffers?: PammOfferType[]
  /** The viewer's own node and its downline; omitted or empty when they are not an agent. */
  agentOffers?: PammOfferType[]
  /** Which perspective the page opens on — the one that matches how the page was reached. */
  defaultView: PammNetworkViewId
  /** The manager's Main Offer and its setter. Reaches the master view only (PT-6881). */
  mainOffer?: MainOfferControl
}

/**
 * The PAMM network with its perspective toggle (PT-5717 / PT-5718 / PT-5719).
 *
 * A view is offered only when the server actually returned data for it, which is what enforces
 * "Master only for masters": the master tree comes from `pammOffers` on a Money Manager account,
 * a field the viewer either owns or is refused (PT-5720). Nothing here filters a wider payload
 * down — the two views are two payloads, so an agent's browser never receives the master tree.
 *
 * The tree, and with it the expand/collapse and open-KPI-panel state, stays mounted across a
 * switch. Both are keyed by offer id, so a node present in both views keeps its state and
 * switching back and forth returns the reader to where they were.
 *
 * @param masterOffers - Offers backing the master view.
 * @param agentOffers - Offers backing the agent view.
 * @param defaultView - Preferred initial view; ignored if that view has no data.
 * @param mainOffer - Main Offer control; passed to the master view only, never to the agent view.
 */
export const PammNetworkTree = ({
  masterOffers = [],
  agentOffers = [],
  defaultView,
  mainOffer,
}: PammNetworkTreeProps) => {
  const offersByView: Record<PammNetworkViewId, PammOfferType[]> = {
    master: masterOffers,
    agent: agentOffers,
  }
  const availableViews = VIEW_ORDER.filter((view) => offersByView[view].length > 0)

  const [selectedView, setSelectedView] = useState<PammNetworkViewId>(defaultView)
  const [recalculating, setRecalculating] = useState(false)

  // The selection can outlive its data (a refetch that drops the master tree, say), so the rendered
  // view is always resolved against what is currently available rather than trusted from state.
  const activeView = availableViews.includes(selectedView) ? selectedView : availableViews[0]

  // Cleared on the next animation frame rather than in this commit, so the placeholders are
  // actually painted instead of being swapped out before the browser ever draws them. On a large
  // network that frame is as long as the rebuild takes, which is precisely what it should cover.
  useEffect(() => {
    if (!recalculating) return undefined
    const frame = requestAnimationFrame(() => setRecalculating(false))
    return () => cancelAnimationFrame(frame)
  }, [recalculating])

  const changeView = (view: PammNetworkViewId): void => {
    setRecalculating(true)
    setSelectedView(view)
  }

  if (availableViews.length === 0) return <PammOffersTree offers={[]} />

  const tree = (
    <PammOffersTree
      offers={offersByView[activeView]}
      recalculating={recalculating}
      mainOffer={activeView === 'master' ? mainOffer : undefined}
    />
  )

  return (
    <Root>
      <NetworkViewHeader view={activeView} />
      {availableViews.length > 1 ? (
        <>
          <NetworkViewToggle views={availableViews} activeView={activeView} onChange={changeView} />
          {/* Only a real tab list gets a tab panel; with a single view there is nothing to label it. */}
          <div id={NETWORK_PANEL_ID} role="tabpanel" aria-labelledby={viewTabId(activeView)}>
            {tree}
          </div>
        </>
      ) : (
        tree
      )}
    </Root>
  )
}
